import { z } from "zod";
import { FirestoreTimestampSchema } from "./common.schema";

/**
 * Hermes conversation docs.
 * Path: organizations/{orgId}/domains/{domainId}/conversations/{threadId}
 *       organizations/{orgId}/domains/{domainId}/conversations/{threadId}/messages/{messageId}
 *
 * Written exclusively by app-hermes-worker (its persistence/firestore_repo.py
 * is the sole writer); the frontend only subscribes.
 *
 * Threads carry `created_by` and messages carry `author` — see the `Actor`
 * dataclass in app-hermes-worker/app/proxy/persistence/models.py. Both are
 * required: the sweep in that repo's scripts/purge_ownerless_conversations.py
 * removed every unstamped document, so a doc without one is an invariant
 * violation, not a legacy shape to tolerate (ADR-0005).
 */

/**
 * Who produced a thread or message. Only a human member has a `uid`; the agent
 * and system record the member they acted for in `on_behalf_of`. The backend
 * always writes all three keys, with explicit nulls.
 */
export const ConversationActorSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("user"),
    uid: z.string().min(1),
    on_behalf_of: z.null().default(null),
  }),
  z.object({
    type: z.literal("agent"),
    uid: z.null().default(null),
    on_behalf_of: z.string().min(1),
  }),
  z.object({
    type: z.literal("system"),
    uid: z.null().default(null),
    on_behalf_of: z.string().min(1),
  }),
]);
export type ConversationActor = z.infer<typeof ConversationActorSchema>;

/** The member a document is attributable to, whoever or whatever produced it. */
export const accountableUid = (actor: ConversationActor): string =>
  actor.type === "user" ? actor.uid : actor.on_behalf_of;

export const ConversationThreadStatusSchema = z.enum([
  "active",
  "archived",
  "errored",
]);
export type ConversationThreadStatus = z.infer<
  typeof ConversationThreadStatusSchema
>;

/**
 * Thread document schema (without id)
 */
export const ConversationThreadDocSchema = z.object({
  mode: z.string(),
  title: z.string().nullable().default(null),
  domain_id: z.string(),
  model_default: z.string(),
  trigger_meta: z.record(z.string(), z.unknown()).default({}),
  status: ConversationThreadStatusSchema,
  created_at: FirestoreTimestampSchema,
  updated_at: FirestoreTimestampSchema,
  last_run_id: z.string().nullable().default(null),
  created_by: ConversationActorSchema,
  /**
   * Organization library files attached to any turn in this conversation.
   *
   * A denormalized index, not the source of truth — the reference itself lives
   * in the turn's own `content_blocks`. It exists so "which conversations used
   * this file" is a query rather than a scan of every message.
   *
   * Ids are kept after a file is deleted: a dangling id records that the
   * conversation referenced something that no longer exists, which is the more
   * useful fact. So this is not a live "is attached" check.
   *
   * Defaulted because threads created before this field existed do not carry it.
   */
  referenced_library_doc_ids: z.array(z.string()).default([]),
});

/**
 * Thread schema (with id)
 */
export const ConversationThreadSchema = ConversationThreadDocSchema.extend({
  thread_id: z.string(),
});
export type ConversationThread = z.infer<typeof ConversationThreadSchema>;

export const ConversationContentBlockSchema = z.looseObject({
  type: z.string(),
  text: z.string().optional(),
});
export type ConversationContentBlock = z.infer<
  typeof ConversationContentBlockSchema
>;

export const AssistantActionEntitySchema = z.object({
  type: z.enum([
    "page",
    "idea",
    "idea_generation",
    "persona",
    "offer",
    "policy",
    "audit_job",
    "ga_insight",
    "wordpress_sync",
    "skill",
    "unknown",
  ]),
  id: z.string().min(1),
  action: z.enum([
    "created",
    "updated",
    "queued",
    "generated",
    "synced",
    "unknown",
  ]),
  label: z.string().optional(),
  status: z.string().optional(),
  related_ids: z.record(z.string(), z.string()).optional(),
  source: z.string().optional(),
});
export type AssistantActionEntity = z.infer<typeof AssistantActionEntitySchema>;

export const ConversationMessageMetaSchema = z.looseObject({
  parent_message_id: z.string().optional(),
  /** The user message that opened this turn. Present on assistant
   * display="final" messages; the ordering key for the thread, since
   * `created_at` is stamped when the turn finished. */
  source_message_id: z.string().nullish(),
  action_entities: z.array(AssistantActionEntitySchema).optional(),
});
export type ConversationMessageMeta = z.infer<
  typeof ConversationMessageMetaSchema
>;

/**
 * Message document schema (without id). Each assistant turn persists twice:
 * display="raw" (verbatim hermes transcript) then display="final" (narrator
 * answer) whose meta.parent_message_id points back at the raw sibling.
 */
export const ConversationMessageDocSchema = z.object({
  role: z.enum(["user", "assistant", "system"]),
  content_blocks: z.array(ConversationContentBlockSchema),
  created_at: FirestoreTimestampSchema,
  run_id: z.string().nullable().default(null),
  is_partial: z.boolean(),
  display: z.enum(["raw", "final"]),
  meta: ConversationMessageMetaSchema.default({}),
  author: ConversationActorSchema,
});

/**
 * Message schema (with id)
 */
export const ConversationMessageSchema = ConversationMessageDocSchema.extend({
  message_id: z.string(),
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

/**
 * Parse functions
 */
export const parseConversationThread = (data: unknown): ConversationThread =>
  ConversationThreadSchema.parse(data);
export const safeConversationThreadParse = (data: unknown) =>
  ConversationThreadSchema.safeParse(data);
export const parseConversationMessage = (data: unknown): ConversationMessage =>
  ConversationMessageSchema.parse(data);
export const safeConversationMessageParse = (data: unknown) =>
  ConversationMessageSchema.safeParse(data);
